import React, { useEffect, useState } from 'react';
import { Plus, Trash2, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import api from '@/utils/api';
import { toast } from 'sonner';

export default function Despesas() {
  const [expenses, setExpenses] = useState([]);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    value: '',
    date: '',
    category: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [totalExpenses, setTotalExpenses] = useState(0);

  useEffect(() => {
    loadExpenses();
  }, []);

  useEffect(() => {
    const total = expenses.reduce((sum, exp) => sum + exp.value, 0);
    setTotalExpenses(total);
  }, [expenses]);

  const loadExpenses = async () => {
    try {
      const response = await api.get('/expenses');
      setExpenses(response.data);
    } catch (error) {
      toast.error('Erro ao carregar despesas');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        value: parseFloat(formData.value),
        date: new Date(formData.date).toISOString()
      };
      await api.post('/expenses', payload);
      toast.success('Despesa criada com sucesso!');
      setIsDialogOpen(false);
      resetForm();
      loadExpenses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar despesa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm('Deseja realmente excluir esta despesa?')) return;

    try {
      await api.delete(`/expenses/${expenseId}`);
      toast.success('Despesa excluída com sucesso!');
      loadExpenses();
    } catch (error) {
      toast.error('Erro ao excluir despesa');
    }
  };

  const resetForm = () => {
    setFormData({
      value: '',
      date: '',
      category: '',
      description: ''
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const categories = [...new Set(expenses.map(e => e.category))].filter(Boolean);

  return (
    <div className="space-y-6" data-testid="despesas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900">Despesas</h1>
          <p className="text-slate-600 mt-1">Gerencie as despesas da agência</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-primary hover:bg-primary/90" data-testid="add-expense-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nova Despesa
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Nova Despesa</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="value">Valor (R$) *</Label>
                  <Input
                    id="value"
                    type="number"
                    step="0.01"
                    value={formData.value}
                    onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                    required
                    data-testid="expense-value-input"
                  />
                </div>
                <div>
                  <Label htmlFor="date">Data *</Label>
                  <Input
                    id="date"
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    required
                    data-testid="expense-date-input"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="category">Categoria *</Label>
                <Input
                  id="category"
                  list="categories"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  placeholder="Ex: Marketing, Equipamento, Software..."
                  required
                  data-testid="expense-category-input"
                />
                <datalist id="categories">
                  {categories.map(cat => (
                    <option key={cat} value={cat} />
                  ))}
                </datalist>
              </div>

              <div>
                <Label htmlFor="description">Descrição *</Label>
                <textarea
                  id="description"
                  className="w-full min-h-[100px] px-3 py-2 text-sm rounded-md border border-input bg-transparent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  data-testid="expense-description-input"
                />
              </div>

              <div className="flex justify-end gap-3">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={loading} data-testid="submit-expense-btn">
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex items-center gap-4">
          <div className="h-12 w-12 rounded-full bg-error/10 flex items-center justify-center text-error">
            <TrendingUp className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wider">Total de Despesas</p>
            <p className="text-3xl font-bold text-slate-900 tracking-tight">{formatCurrency(totalExpenses)}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg border border-slate-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50/50 border-b border-slate-100">
              <tr>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Data</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Categoria</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Descrição</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Valor</th>
                <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Ações</th>
              </tr>
            </thead>
            <tbody>
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-slate-500">
                    Nenhuma despesa encontrada
                  </td>
                </tr>
              ) : (
                expenses.map((expense) => (
                  <tr key={expense.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`expense-row-${expense.id}`}>
                    <td className="p-4 align-middle text-sm text-slate-600">{formatDate(expense.date)}</td>
                    <td className="p-4 align-middle text-sm font-medium text-slate-700">{expense.category}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{expense.description}</td>
                    <td className="p-4 align-middle text-sm font-mono text-slate-900">{formatCurrency(expense.value)}</td>
                    <td className="p-4 align-middle text-sm">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(expense.id)}
                        data-testid={`delete-expense-${expense.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}